import { codeStatusOf, codeProblemOf } from '../../domain/codeStatus';
import { lastSessionOf } from '../../domain/codeChallenge';
import type { HeldUntil } from '../../domain/codeChallenge';
import type { AlertChannel, CodePurpose, OpenedSession, PasscodeChannel } from '../../types/domain.types';
import { useDeadlinePassed } from '../code/useDeadlinePassed';

import { fieldFailures, NO_FAILURES, passwordFailures, resendProblemOf } from './loginFailures';
import type { LoginFailures } from './loginFailures';
import { askKeyOf, codeSessionOf, submittableOf, viewScreenOf } from './loginMachine';
import type { LoginScreen } from './loginMachine';
import type { HoldView, LoginBusy, LoginLogic, LoginNoticeView, LoginView } from './loginView';
import { useCodeSend } from './useCodeSend';
import { useCodeVerify } from './useCodeVerify';
import { useIdentifierField } from './useIdentifierField';
import { useLoginMachine } from './useLoginMachine';
import { usePasswordLogin } from './usePasswordLogin';

export { passcodeMutationKeys } from './mutationKeys';
export type { LoginScreen } from './loginMachine';
export type { HoldView, IdentifierView, LoginBusy, LoginLogic, LoginNoticeView, LoginView } from './loginView';

export interface LoginLogicOptions {
  readonly onVerified?: (session: OpenedSession) => void;
  readonly onCodeSent?: () => void;
  readonly showCode?: boolean;
}

const holdViewOf = (hold: HeldUntil | null, key: string | null): HoldView | null =>
  hold !== null && key !== null && hold.key === key ? { reason: hold.reason, seconds: hold.seconds, until: hold.until } : null;

const failuresOf = (screen: LoginScreen, errors: { send: Error | null; lookup: Error | null; login: Error | null }): LoginFailures => {
  switch (screen) {
    case 'password':
      return passwordFailures(errors.login ?? errors.send);
    case 'identify':
    case 'noPassword':
      return fieldFailures(errors.send ?? errors.lookup);
    default:
      return NO_FAILURES;
  }
};

interface BusyFlags {
  readonly sending: boolean;
  readonly channel: PasscodeChannel | null;
  readonly lookingUp: boolean;
  readonly loggingIn: boolean;
  readonly verifying: boolean;
}

const busyOf = (flags: BusyFlags): LoginBusy => {
  if (flags.sending) return { kind: 'sending', channel: flags.channel };
  if (flags.lookingUp) return { kind: 'lookingUp' };
  if (flags.loggingIn) return { kind: 'loggingIn' };
  return flags.verifying ? { kind: 'verifying' } : { kind: 'idle' };
};

export const useLoginLogic = ({ onVerified, onCodeSent, showCode = true }: LoginLogicOptions = {}): LoginLogic => {
  const machine = useLoginMachine();
  const { state, dispatch } = machine;
  const submittable = submittableOf(state.field.value);
  const screen = viewScreenOf(state, showCode);

  const onOpened = (session: OpenedSession): void => {
    dispatch({ type: 'sessionOpened', session });
    if (session.passwordGrant === null) onVerified?.(session);
  };

  const sender = useCodeSend(machine, { onCodeSent });
  const verifier = useCodeVerify(machine, { onOpened });
  const password = usePasswordLogin(machine, { submittable, onOpened });
  const failures = failuresOf(screen, { send: sender.error, lookup: password.lookupError, login: password.loginError });
  const field = useIdentifierField(machine, { submittable, onField: screen === 'identify', lookedUp: password.lookedUp, refusal: failures.field });

  const codeSession = screen === 'code' ? codeSessionOf(state) : null;
  const stateHold = state.challenge.hold;
  const holdPassed = useDeadlinePassed(stateHold?.until ?? null);
  const expired = useDeadlinePassed(codeSession?.expiresAt ?? null);
  const liveHold = holdPassed ? null : stateHold;

  const keyFor = (purpose: CodePurpose): string | null => (submittable === null ? null : askKeyOf({ identifier: submittable, purpose }));
  const formPurpose: CodePurpose = screen === 'password' ? 'PASSWORD_RESET' : 'LOGIN';
  const formHold = holdViewOf(stateHold, keyFor(formPurpose));
  const heldNow = holdViewOf(liveHold, keyFor(formPurpose)) !== null;
  const waiting = sender.paused || verifier.paused || password.paused;
  const notice: LoginNoticeView = {
    waiting,
    hold: formHold,
    problem: failures.form,
    passwordOffered: screen === 'identify' && formHold?.reason === 'TOO_MANY_SENDS' && field.passwordSet !== false,
  };

  const viewOf = (): LoginView => {
    const { step } = state;
    if (codeSession !== null) {
      const sessionHold = liveHold !== null && liveHold.key === codeSession.key ? liveHold : null;
      return {
        screen: 'code',
        session: codeSession,
        status: codeStatusOf({ verifying: verifier.pending, problem: codeProblemOf(verifier.error), hold: sessionHold, expired }),
        resendProblem: resendProblemOf(sender.error),
        waiting,
      };
    }
    switch (step.screen) {
      case 'password':
        return {
          screen: 'password',
          field: field.view,
          password: step.password,
          passwordMissing: step.missingKey > 0 && step.password === '',
          passwordProblemKey: step.missingKey,
          passwordRefusal: failures.password,
          held: heldNow,
          notice,
        };
      case 'noPassword':
        return { screen: 'noPassword', reason: step.reason, field: field.view, codeChoices: field.codeChoices, held: heldNow, notice };
      case 'locked':
        return { screen: 'locked', lockUntil: step.lockUntil, lockMinutes: step.lockMinutes };
      case 'newPassword':
        return { screen: 'newPassword', grant: step.grant };
      case 'done':
        return { screen: 'done' };
      case 'identify':
        return {
          screen: 'identify',
          field: field.view,
          codeChoices: field.codeChoices,
          held: heldNow,
          retrying: failures.form?.kind === 'transport',
          notice,
        };
    }
  };

  const request = (purpose: CodePurpose, picked?: AlertChannel): void => {
    if (submittable === null) {
      dispatch({ type: 'problemFound' });
      return;
    }
    verifier.clear();
    password.clearLogin();
    sender.request({ identifier: submittable, purpose, channel: purpose === 'LOGIN' ? picked : undefined }, true);
  };

  const verify = (code?: string): void => {
    if (codeSession === null) return;
    sender.clear();
    verifier.verify(codeSession, code);
  };

  const logIn = (): void => {
    if (state.step.screen !== 'password') return;
    sender.clear();
    password.clearLookup();
    password.logIn(submittable, state.step.password);
  };

  return {
    view: viewOf(),
    busy: busyOf({
      sending: sender.pending,
      channel: sender.channel,
      lookingUp: password.lookingUp,
      loggingIn: password.loggingIn,
      verifying: verifier.pending,
    }),
    actions: {
      setIdentifier: (value) => {
        if (password.loggingIn) return;
        field.type(value);
        sender.clear();
        password.clearLookup();
        password.clearLogin();
      },
      setPassword: (value) => {
        if (password.loggingIn) return;
        dispatch({ type: 'passwordTyped', password: value });
        password.clearLogin();
      },
      setCode: (value) => {
        if (verifier.pending) return;
        dispatch({ type: 'codeTyped', code: value });
        sender.clear();
      },
      send: (channel) => {
        if (screen === 'identify' || screen === 'noPassword') request('LOGIN', channel);
      },
      resend: () => {
        if (codeSession === null) return;
        verifier.clear();
        sender.request(codeSession.target, false);
      },
      verify,
      choosePassword: () => {
        sender.clear();
        password.clearLookup();
        password.choosePassword(submittable);
      },
      logIn,
      forgotPassword: () => {
        if (screen === 'password') request('PASSWORD_RESET');
      },
      retry: () => {
        if (screen === 'code' && verifier.error !== null) {
          verify();
          return;
        }
        if (screen === 'password' && password.loginError !== null) {
          logIn();
          return;
        }
        sender.retryLast();
      },
      sendNewCode: () => {
        if (state.step.screen !== 'locked' || submittable === null) return;
        const loginKey = askKeyOf({ identifier: submittable, purpose: 'LOGIN' });
        const last = lastSessionOf(state.challenge.phase);
        sender.request({ identifier: submittable, purpose: 'LOGIN', channel: last?.key === loginKey ? last.sent.sentOn : undefined }, true);
      },
      back: () => {
        if (verifier.pending || password.loggingIn) return;
        dispatch({ type: 'back' });
      },
      finish: () => {
        if (state.step.screen !== 'newPassword') return;
        dispatch({ type: 'finished' });
        onVerified?.(state.step.session);
      },
    },
  };
};
